import React, { useState } from 'react';
import { useAppContext } from '../App';
import { isValidEmail, validatePassword } from '../utils/security';

const AuthModal = () => {
  const { isAuthModalOpen, setIsAuthModalOpen, authMode, setAuthMode, signIn, signUp, showNotification } = useAppContext();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isAuthModalOpen) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setCompany('');
    setErrors({});
    setShowPassword(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    setIsAuthModalOpen(false);
  };

  const handleSwitchMode = (mode) => {
    resetForm();
    setAuthMode(mode);
  };

  const validateSignInForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignUpForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.errors[0];
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!validateSignInForm()) return;

    setIsSubmitting(true);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const result = signIn(email, password);

    if (result.success) {
      resetForm();
    }

    setIsSubmitting(false);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateSignUpForm()) return;

    setIsSubmitting(true);

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const result = signUp({ email, password, name, company });

    if (result.success) {
      resetForm();
    }

    setIsSubmitting(false);
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="auth-header">
          <div className="auth-logo">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="authGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
              </defs>
              <path d="M14 8 L14 16 L8 32 C7 34 8 36 10 36 L30 36 C32 36 33 34 32 32 L26 16 L26 8" stroke="url(#authGradient)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="13" y="4" width="14" height="4" rx="1" fill="url(#authGradient)"/>
            </svg>
          </div>
          <h2 className="auth-title">
            {authMode === 'signin' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="auth-subtitle">
            {authMode === 'signin'
              ? 'Sign in to access your account'
              : 'Join AsiaQuimicos marketplace'}
          </p>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${authMode === 'signin' ? 'active' : ''}`}
            onClick={() => handleSwitchMode('signin')}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${authMode === 'signup' ? 'active' : ''}`}
            onClick={() => handleSwitchMode('signup')}
          >
            Sign Up
          </button>
        </div>

        {authMode === 'signin' ? (
          <form className="auth-form" onSubmit={handleSignIn}>
            <div className="form-group">
              <label htmlFor="signin-email">Email</label>
              <input
                id="signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={errors.email ? 'error' : ''}
                disabled={isSubmitting}
                autoComplete="email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="signin-password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="signin-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={errors.password ? 'error' : ''}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleSignUp}>
            <div className="form-group">
              <label htmlFor="signup-name">Full Name</label>
              <input
                id="signup-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                className={errors.name ? 'error' : ''}
                disabled={isSubmitting}
                autoComplete="name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className={errors.email ? 'error' : ''}
                disabled={isSubmitting}
                autoComplete="email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="signup-company">Company (Optional)</label>
              <input
                id="signup-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Enter your company name"
                disabled={isSubmitting}
                autoComplete="organization"
              />
            </div>

            <div className="form-group">
              <label htmlFor="signup-password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  className={errors.password ? 'error' : ''}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
              <div className="password-requirements">
                <p>Password must have:</p>
                <ul>
                  <li className={password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[a-z]/.test(password) ? 'met' : ''}>One lowercase letter</li>
                  <li className={/[A-Z]/.test(password) ? 'met' : ''}>One uppercase letter</li>
                  <li className={/[0-9]/.test(password) ? 'met' : ''}>One number</li>
                </ul>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="signup-confirm">Confirm Password</label>
              <input
                id="signup-confirm"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className={errors.confirmPassword ? 'error' : ''}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <button
              type="submit"
              className="btn btn-primary auth-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        )}

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.8);
            backdrop-filter: blur(8px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--space-lg);
            animation: fadeIn 0.2s ease;
          }

          .auth-modal {
            background: linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98));
            border: 1px solid rgba(59, 130, 246, 0.3);
            border-radius: var(--radius-xl);
            width: 100%;
            max-width: 440px;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
            animation: scaleIn 0.3s ease;
            padding: var(--space-xl);
          }

          .auth-header {
            text-align: center;
            margin-bottom: var(--space-lg);
          }

          .auth-logo {
            width: 60px;
            height: 60px;
            margin: 0 auto var(--space-md);
          }

          .auth-logo svg {
            width: 100%;
            height: 100%;
          }

          .auth-title {
            font-family: var(--font-display);
            font-size: var(--font-2xl);
            font-weight: 700;
            color: var(--color-neutral-50);
            margin-bottom: var(--space-xs);
          }

          .auth-subtitle {
            font-size: var(--font-sm);
            color: var(--color-neutral-400);
          }

          .auth-tabs {
            display: flex;
            gap: var(--space-xs);
            margin-bottom: var(--space-lg);
            background: rgba(255, 255, 255, 0.05);
            padding: var(--space-xs);
            border-radius: var(--radius-md);
          }

          .auth-tab {
            flex: 1;
            padding: var(--space-sm) var(--space-md);
            background: transparent;
            border: none;
            border-radius: var(--radius-sm);
            color: var(--color-neutral-400);
            font-family: var(--font-body);
            font-size: var(--font-sm);
            font-weight: 500;
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .auth-tab:hover {
            color: var(--color-neutral-200);
          }

          .auth-tab.active {
            background: rgba(59, 130, 246, 0.2);
            color: var(--color-accent-400);
          }

          .auth-form {
            display: flex;
            flex-direction: column;
            gap: var(--space-md);
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: var(--space-xs);
          }

          .form-group label {
            font-size: var(--font-sm);
            font-weight: 500;
            color: var(--color-neutral-300);
          }

          .form-group input {
            padding: var(--space-sm) var(--space-md);
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: var(--radius-md);
            color: var(--color-neutral-100);
            font-family: var(--font-body);
            font-size: var(--font-base);
            transition: all var(--transition-fast);
          }

          .form-group input:focus {
            outline: none;
            border-color: var(--color-accent-400);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
          }

          .form-group input.error {
            border-color: var(--color-error);
          }

          .form-group input::placeholder {
            color: var(--color-neutral-500);
          }

          .password-input-wrapper {
            position: relative;
          }

          .password-input-wrapper input {
            width: 100%;
            padding-right: 44px;
          }

          .password-toggle {
            position: absolute;
            right: var(--space-sm);
            top: 50%;
            transform: translateY(-50%);
            background: transparent;
            border: none;
            color: var(--color-neutral-400);
            cursor: pointer;
            padding: var(--space-xs);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color var(--transition-fast);
          }

          .password-toggle:hover {
            color: var(--color-neutral-200);
          }

          .error-message {
            font-size: var(--font-xs);
            color: var(--color-error);
            margin-top: var(--space-xs);
          }

          .password-requirements {
            margin-top: var(--space-sm);
            padding: var(--space-sm);
            background: rgba(255, 255, 255, 0.03);
            border-radius: var(--radius-sm);
          }

          .password-requirements p {
            font-size: var(--font-xs);
            color: var(--color-neutral-400);
            margin-bottom: var(--space-xs);
          }

          .password-requirements ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .password-requirements li {
            font-size: var(--font-xs);
            color: var(--color-neutral-500);
            padding: 2px 0;
            padding-left: var(--space-md);
            position: relative;
          }

          .password-requirements li::before {
            content: '○';
            position: absolute;
            left: 0;
            color: var(--color-neutral-500);
          }

          .password-requirements li.met {
            color: var(--color-success);
          }

          .password-requirements li.met::before {
            content: '●';
            color: var(--color-success);
          }

          .auth-submit {
            width: 100%;
            padding: var(--space-md);
            margin-top: var(--space-sm);
            font-size: var(--font-base);
          }

          .auth-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AuthModal;
